import React, { useState, useEffect, useMemo } from "react";
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  query, where, serverTimestamp, arrayUnion, getDoc, setDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { S, fmtDate } from "../crm/theme";
import DateBox from "../crm/DateBox";
import { buildSingleExport, buildShownExport, buildProjectsExport, buildSummaryTable, SUMMARY_ROWS, toMs, planImport, downloadJson, priorityRank, RELEASES, PRIORITIES } from "../crm/trackerFile";
import { buildWordFile, downloadBytes, WORD_MIME } from "../crm/trackerWord";
import { RULES_MAX, looksLikeRealSecret, describeChange, withRules } from "../crm/trackerRules";
import TrackerDocsTab from "./TrackerDocsTab";
import TrackerAuditTab from "./TrackerAuditTab";

// Project Tracker. Step 1 of the plan in Section 30 of the audit file.
// Data: collection "projectTracker", one document per task.
//  - The Owner sees and changes everything.
//  - Any other CRM user can send a task request and sees only their own
//    requests with the status (the database rule enforces this, not just this screen).
//  - A Sales Head sees the whole tracker read-only (no Export, Import, Word or Rules) and can change only the
//    wording of tasks that he and the people he added created. Security tasks: only ongolesri99@gmail.com sees them.

const OWNER_EMAIL = "layakarudu@gmail.com";
// Only this Sales Head may see the Security tasks (the database rule checks this too).
const SECURITY_VIEWER_EMAIL = "ongolesri99@gmail.com";
const PROJECTS = ["Main app", "CRM", "Billing app", "Website", "All projects"];
const TYPES = ["Security", "Normal"];
const STATUSES = ["Pending", "Doing", "Check", "Done", "Parked"];
const RELEASE_COLOR = { "Before pilot": "#f87171", "After pilot": "#4f8ef7", Later: "#7b82a0" };
const PRIORITY_COLOR = { High: "#f87171", Medium: "#fbbf24", Low: "#3ecf8e" };
const STATUS_ORDER = { Doing: 0, Check: 1, Pending: 2, Parked: 3, Done: 4 };
// Words shown on screen. The saved values (Pending, Doing, Done, Parked) stay the same in the database.
// "Open" is only a filter choice: it means Pending in the summary table (To do + In progress + Needs check + On hold).
// Status filter words, same as the summary table: Pending = To do + In progress + Needs check + On hold.
const FILTER_STATUS_LABEL = { All: "All", Open: "Pending (all)", Pending: "Pending: To do", Doing: "Pending: In progress", Check: "Pending: Needs check", Parked: "Pending: On hold", Done: "Done" };
const STATUS_LABEL = { Pending: "To do", Doing: "In progress", Check: "Needs check", Parked: "On hold", Done: "Done", Open: "Pending (all open)" };
// Release words on screen (saved values stay Before pilot, After pilot, Later, empty).
// The words of the summary table: Before release, After release, Wish list, Not tasks (no Release set).
const RELEASE_LABEL = { "Before pilot": "Before release", "After pilot": "After release", Later: "Wish list" };
const relLabel = (r) => (r === "All" ? "All" : r === "Not set" || !r ? "Not tasks" : r === "Post+Backlog" ? "After release + Wish list" : RELEASE_LABEL[r] || r);
// Search box: the text of a task that the search looks in (number, title, project, type, status, release, link, note, details, added by).
const taskSearchText = (t) =>
  [
    t.num ? "T-" + String(t.num).padStart(3, "0") : "",
    t.title, t.project, t.type, t.status, FILTER_STATUS_LABEL[t.status], t.release, relLabel(t.release), t.priority,
    t.link, t.note, t.details, t.doneWhen, t.targetDate, t.createdBy,
  ].filter(Boolean).join(" \n ").toLowerCase();
// Does the task fit the Status filter? ("Open" = every status except Done = Pending in the summary table)
const statusOk = (f, t) => (f === "All" ? true : f === "Open" ? t.status !== "Done" : t.status === f);
// Does the task fit the Release filter? ("Post+Backlog" = After pilot and Later together)
const relOk = (f, t) => (f === "All" ? true : f === "Not set" ? !t.release : f === "Post+Backlog" ? t.release === "After pilot" || t.release === "Later" : t.release === f);
// Does the task fit the Priority filter? ("Not set" = no Priority chosen)
const prioOk = (f, t) => (f === "All" ? true : f === "Not set" ? !t.priority : t.priority === f);
const STATUS_COLOR = { Pending: "#fbbf24", Doing: "#4f8ef7", Check: "#a78bfa", Done: "#3ecf8e", Parked: "#7b82a0" };

// Today in India time as 2026-10-28. A task is late when its Target date is before today and it is not Done.
const todayIst = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
const fmtTarget = (d) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d || ""); return m ? m[3] + "-" + MONTHS[Number(m[2]) - 1] + "-" + m[1] : (d || "—"); };
const targetColor = (t) => {
  if (!t.targetDate || t.status === "Done") return "#7b82a0";
  const now = todayIst();
  if (t.targetDate < now) return "#f87171"; // late
  const soon = new Date(Date.now() + 7 * 86400000);
  return t.targetDate <= new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(soon) ? "#fbbf24" : "#c8cce0"; // due within 7 days
};
const TYPE_COLOR = { Security: "#f87171", Normal: "#7b82a0" };

// Same words as Rule 6 of Start Here. Only a warning: the person decides.
const SECRET_WORDS = /secret|passw|token|private_key|apikey/i;
const SECRET_WARNING =
  "This text contains a word like password, token, secret or key. " +
  "Never store a real password, key or token here. Save anyway?";

// "Updated" column: date and time in India time, like 10-Oct-2026 09:43.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtUpdated(ts) {
  try {
    const d = ts && typeof ts.toDate === "function" ? ts.toDate() : ts && ts.seconds ? new Date(ts.seconds * 1000) : null;
    if (!d || isNaN(d.getTime())) return "—";
    const p = {};
    new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(d).forEach((x) => { p[x.type] = x.value; });
    return `${p.day}-${MONTHS[Number(p.month) - 1]}-${p.year} ${p.hour}:${p.minute}`;
  } catch (e) {
    return "—";
  }
}

// The Export file is built in src/crm/trackerFile.js (buildProjectsExport): one or more projects ticked, ONE file.

const blank = () => ({
  title: "", project: "Main app", type: "Normal", status: "Pending",
  link: "", note: "", details: "", release: "", priority: "", doneWhen: "", targetDate: "",
});

function TrackerModal({ task, isOwner, onSave, onClose, onDelete, onExportOne }) {
  const [form, setForm] = useState(task ? { ...blank(), ...task } : blank());
  const [newLine, setNewLine] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const history = Array.isArray(task?.history) ? task.history : [];

  const handleSave = async () => {
    if (!form.title.trim()) { setError("Title is required."); return; }
    const texts = [form.title, form.link, form.note, form.details, form.doneWhen, newLine].join("\n");
    if (SECRET_WORDS.test(texts) && !window.confirm(SECRET_WARNING)) return;

    const adds = [];
    if (task && task.status !== form.status) adds.push("Status: " + (STATUS_LABEL[task.status] || task.status) + " → " + (STATUS_LABEL[form.status] || form.status));
    if (task && (task.targetDate || "") !== (form.targetDate || "")) adds.push("Target date: " + (task.targetDate || "-") + " → " + (form.targetDate || "-"));
    if (task && (task.doneWhen || "") !== (form.doneWhen || "").trim()) adds.push("Done when: " + (task.doneWhen ? "changed" : "set"));
    if (newLine.trim()) adds.push(newLine.trim());

    setSaving(true);
    setError("");
    try {
      await onSave(
        {
          title: form.title.trim(), project: form.project, type: form.type, status: form.status,
          link: form.link.trim(), note: form.note.trim(), details: form.details,
          ...(isOwner ? { release: form.release || "", priority: form.priority || "", doneWhen: (form.doneWhen || "").trim(), targetDate: form.targetDate || "" } : {}),
        },
        adds
      );
      onClose();
    } catch (e) {
      setError("Save failed: " + e.message);
    }
    setSaving(false);
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <div style={S.title}>{task ? "Edit Task" : isOwner ? "New Task" : "New Task Request"}</div>
          <button style={S.btnSmall} onClick={onClose}>✕ Close</button>
        </div>

        {error && <div style={{ ...S.msg, borderColor: "#f87171", color: "#f87171" }}>{error}</div>}

        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Title *</label>
          <input style={S.input} value={form.title} onChange={(e) => set("title", e.target.value)} />
        </div>

        <div style={S.formGrid}>
          <div>
            <label style={S.label}>Project</label>
            <select style={S.select} value={form.project} onChange={(e) => set("project", e.target.value)}>
              {PROJECTS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          {isOwner && (
            <>
              <div>
                <label style={S.label}>Type</label>
                <select style={S.select} value={form.type} onChange={(e) => set("type", e.target.value)}>
                  {TYPES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Status</label>
                <select style={S.select} value={form.status} onChange={(e) => set("status", e.target.value)}>
                  {STATUSES.map((p) => <option key={p} value={p}>{STATUS_LABEL[p]}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Release</label>
                <select style={S.select} value={form.release || ""} onChange={(e) => set("release", e.target.value)}>
                  <option value="">Not tasks</option>
                  {RELEASES.map((p) => <option key={p} value={p}>{RELEASE_LABEL[p]}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Priority</label>
                <select style={S.select} value={form.priority || ""} onChange={(e) => set("priority", e.target.value)}>
                  <option value="">Not set</option>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Target date</label>
                <DateBox value={form.targetDate || ""} onChange={(v) => set("targetDate", v)} placeholder="Pick a date" />
              </div>
            </>
          )}
        </div>

        {isOwner && (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Done when… (one line: how you will know this task is really finished)</label>
              <input style={S.input} maxLength={300} value={form.doneWhen || ""} onChange={(e) => set("doneWhen", e.target.value)} placeholder="e.g. A new member can sign up on a phone and sees the dashboard" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Link (section or finding of the Word file)</label>
              <input style={S.input} value={form.link} onChange={(e) => set("link", e.target.value)} placeholder="e.g. Finding 2, Sec 21" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Note (short; the person who sent the task can read this)</label>
              <input style={S.input} value={form.note} onChange={(e) => set("note", e.target.value)} />
            </div>
          </>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>{isOwner ? "Details (full text)" : "What do you need? (details)"}</label>
          <textarea style={S.textarea} rows={isOwner ? 10 : 6} value={form.details} onChange={(e) => set("details", e.target.value)} />
        </div>

        {isOwner && (
          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>History (dated lines, added only)</label>
            {history.length === 0 ? (
              <div style={{ fontSize: 12, color: "#7b82a0", marginBottom: 8 }}>No history yet.</div>
            ) : (
              <div style={{ marginBottom: 8 }}>
                {history.map((h, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#c8cce0", padding: "3px 0" }}>
                    <span style={{ color: "#7b82a0" }}>{fmtDate(h.at)}</span> — {h.text}
                  </div>
                ))}
              </div>
            )}
            <input
              style={S.input}
              value={newLine}
              onChange={(e) => setNewLine(e.target.value)}
              placeholder="Add a line to the history (saved with today's date)"
            />
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <div>
            {isOwner && task && onDelete && (
              <button style={S.btnRed} onClick={() => onDelete(task.id)}>Delete Task</button>
            )}
            {isOwner && task && onExportOne && (
              <button style={{ ...S.btnGray, marginLeft: 8 }} onClick={() => onExportOne(task)}>Export this task</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.btnGray} onClick={onClose}>Cancel</button>
            <button style={S.btnBlue} onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : isOwner ? "Save Task" : task ? "Save changes" : "Send Request"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Rules page (Step 4): one text box, saved in collection "trackerRules", document "main". Owner only.
async function readRules() {
  const s = await getDoc(doc(db, "trackerRules", "main"));
  return s.exists() ? s.data() : { text: "", history: [] };
}

function RulesModal({ onClose, onMsg }) {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await readRules();
        setText(r.text || "");
        setSaved(r.text || "");
        setHistory(Array.isArray(r.history) ? r.history : []);
      } catch (e) {
        setError("Could not load the Rules: " + e.message);
      }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    if (text.length > RULES_MAX) { setError("The Rules are too long (over " + RULES_MAX + " characters)."); return; }
    const line = describeChange(saved, text);
    if (!line) { setError("Nothing has changed."); return; }
    if (looksLikeRealSecret(text) && !window.confirm(SECRET_WARNING)) return;
    setSaving(true);
    setError("");
    try {
      const entry = { at: new Date().toISOString(), text: line };
      await setDoc(
        doc(db, "trackerRules", "main"),
        { text, updatedAt: serverTimestamp(), history: arrayUnion(entry) },
        { merge: true }
      );
      setSaved(text);
      setHistory((h) => h.concat(entry));
      onMsg("Rules saved.");
    } catch (e) {
      setError("Save failed: " + e.message);
    }
    setSaving(false);
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <div style={S.title}>📜 Rules</div>
          <button style={S.btnSmall} onClick={onClose}>✕ Close</button>
        </div>
        <div style={{ fontSize: 12, color: "#7b82a0", marginBottom: 10 }}>
          Start Here, How changes are made, and Projects. Only the Owner can see this page. Export carries these Rules to Claude.
        </div>
        {error && <div style={{ ...S.msg, borderColor: "#f87171", color: "#f87171" }}>{error}</div>}
        {loading ? (
          <div style={{ color: "#7b82a0" }}>Loading…</div>
        ) : (
          <>
            <textarea style={S.textarea} rows={18} value={text} onChange={(e) => setText(e.target.value)} />
            <div style={{ fontSize: 12, color: "#7b82a0", margin: "4px 0 10px" }}>{text.length} characters</div>
            <label style={S.label}>History (dated lines, added only)</label>
            {history.length === 0 ? (
              <div style={{ fontSize: 12, color: "#7b82a0", marginBottom: 8 }}>No history yet.</div>
            ) : (
              <div style={{ marginBottom: 8, maxHeight: 120, overflowY: "auto" }}>
                {history.map((h, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#c8cce0", padding: "3px 0" }}>
                    <span style={{ color: "#7b82a0" }}>{fmtDate(h.at)}</span> — {h.text}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button style={S.btnGray} onClick={onClose}>Close</button>
          <button style={S.btnBlue} onClick={handleSave} disabled={saving || loading}>{saving ? "Saving…" : "Save Rules"}</button>
        </div>
      </div>
    </div>
  );
}

// The summary table (same layout as the Excel). Used on the screen (clickable) and in the Import preview (with "was" numbers).
const GRID_COLS = [
  // [key, short heading, colour, Release filter, Status filter, Type filter]
  ["beforeDone", "Done", "#3ecf8e", "Before pilot", "Done", "All"],
  ["beforePending", "Pending", "#fbbf24", "Before pilot", "Open", "All"],
  ["afterDone", "Done", "#3ecf8e", "After pilot", "Done", "All"],
  ["afterPending", "Pending", "#fbbf24", "After pilot", "Open", "All"],
  ["wishList", "Wish list", "#7b82a0", "Later", "All", "All"],
  ["notTasks", "Not tasks", "#7b82a0", "Not set", "All", "All"],
  ["total", "Total", "#e8eaf6", "All", "All", "All"],
  ["securityDone", "Done", "#3ecf8e", "All", "Done", "Security"],
  ["securityPending", "Pending", "#fbbf24", "All", "Open", "Security"],
];

function SummaryGrid({ table, base, onCell, dates, onDate }) {
  const baseRows = base ? base.rows.concat([base.total]) : [];
  const head = { ...S.th, textAlign: "center", whiteSpace: "nowrap" };
  const LINE = "2px solid #f87171"; // the vertical line before the Security columns
  const group = { ...head, borderBottom: "1px solid #2a2d3e" };
  return (
    <div style={{ ...S.tableWrap, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
        <thead>
          <tr>
            <th style={{ ...S.th, whiteSpace: "nowrap" }} rowSpan={2}>Project</th>
            {dates && <th style={head} rowSpan={2}>Release date</th>}
            <th style={group} colSpan={2}>Before release</th>
            <th style={group} colSpan={2}>After release</th>
            <th style={head} rowSpan={2}>Wish list</th>
            <th style={head} rowSpan={2}>Not tasks</th>
            <th style={head} rowSpan={2}>Total</th>
            <th style={{ ...group, borderLeft: LINE }} colSpan={2}>Security</th>
          </tr>
          <tr>
            {[0, 1, 2, 3, 7, 8].map((i) => <th key={i} style={i === 7 ? { ...head, borderLeft: LINE } : head}>{GRID_COLS[i][1]}</th>)}
          </tr>
        </thead>
        <tbody>
          {table.rows.concat([table.total]).map((r) => {
            const isTotal = r.project === "Total";
            const found = SUMMARY_ROWS.find(([label]) => label === r.project);
            const proj = isTotal ? "All" : found ? found[1] : null; // "Other projects" has no filter
            const old = baseRows.find((b) => b.project === r.project);
            return (
              <tr key={r.project} style={isTotal ? { background: "rgba(255,255,255,0.04)" } : undefined}>
                <td
                  style={{ ...S.td, fontWeight: 700, color: "#e8eaf6", whiteSpace: "nowrap", cursor: onCell && proj ? "pointer" : "default" }}
                  onClick={onCell && proj ? () => onCell(proj, "All", "All", "All") : undefined}
                >
                  {r.project === "Total" ? "Overall" : r.project}
                  {(() => {
                    const bt = r.beforeDone + r.beforePending;
                    if (bt === 0) return null;
                    const pct = Math.round((r.beforeDone * 100) / bt);
                    return (
                      <div
                        style={{ minWidth: 170, marginTop: 4, fontWeight: 400 }}
                        title="Before release tasks that are Done"
                      >
                        <div style={{ fontSize: 12, color: "#e8eaf6" }}>
                          <span style={{ color: "#3ecf8e", fontWeight: 700 }}>{r.beforeDone}</span> of {bt} done · {pct}%
                        </div>
                        <div style={{ height: 6, background: "#2a2d3e", borderRadius: 6, overflow: "hidden", marginTop: 3 }}>
                          <div style={{ width: pct + "%", height: "100%", background: "#3ecf8e" }} />
                        </div>
                      </div>
                    );
                  })()}
                </td>
                {dates && (
                  <td style={{ ...S.td, textAlign: "center" }}>
                    {proj && !isTotal ? (
                      <DateBox
                        style={{ width: 140, padding: "4px 8px", fontSize: 13 }}
                        value={dates[proj] || ""}
                        onChange={(v) => onDate(proj, v)}
                        title="Release date of this project (you can change it here)"
                      />
                    ) : null}
                  </td>
                )}
                {GRID_COLS.map(([k, , color, rel, st, ty]) => {
                  const n = r[k];
                  const was = base ? (old ? old[k] : 0) : n;
                  const click = onCell && proj && n > 0;
                  return (
                    <td key={k} style={{ ...S.td, textAlign: "center", fontWeight: isTotal || k === "total" ? 700 : 400, ...(k === "securityDone" ? { borderLeft: LINE } : {}) }}>
                      <span
                        onClick={click ? () => onCell(proj, rel, st, ty) : undefined}
                        title={click ? "Click to list these tasks" : undefined}
                        style={{ fontSize: 16, fontWeight: 700, color: n > 0 ? color : "#4a4f6a", cursor: click ? "pointer" : "default" }}
                      >
                        {n}
                      </span>
                      {was !== n && <span style={{ fontSize: 11, color: "#fbbf24" }}> (was {was})</span>}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function ProjectTrackerScreen({ user }) {
  const isRealOwner = (user?.email || "").trim().toLowerCase() === OWNER_EMAIL;
  // Sales Head (18 Oct 2026): sees the whole tracker read-only and changes only the tasks he and his team created.
  const isHead = user?.role === "SalesHead";
  const isKrishna = (user?.email || "").trim().toLowerCase() === SECURITY_VIEWER_EMAIL;
  // "isOwner" below means "sees the full tracker screen" (the Owner or a Sales Head).
  // Everything that only the Owner may do uses isRealOwner.
  const isOwner = isRealOwner || isHead;
  const [teamUids, setTeamUids] = useState([]); // the people this Sales Head added (their tasks he may change)
  const canEdit = (t) => isRealOwner || (!!user?.uid && (t.createdByUid === user.uid || (isHead && teamUids.includes(t.createdByUid))));
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new task
  const [view, setView] = useState("tasks"); // "tasks" or "docs" (docs: Owner only)
  const [fProject, setFProject] = useState("All");
  const [fType, setFType] = useState("All");
  const [fStatus, setFStatus] = useState("All");
  const [fRelease, setFRelease] = useState("All");
  const [fPriority, setFPriority] = useState("All");
  const [showDone, setShowDone] = useState(false);
  const [search, setSearch] = useState(""); // the Search box: every word typed must be found in the task
  const [plan, setPlan] = useState(null); // the dry-run result of an import (nothing is saved until Confirm)
  const [importing, setImporting] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [pickExport, setPickExport] = useState(false); // the "which project?" box of the Export button
  const [exportRelease, setExportRelease] = useState("Before pilot"); // which release the Export holds: Before pilot, After pilot, Later or All
  const [exportPriority, setExportPriority] = useState("All"); // which Priority the Export holds: High, Medium, Low, Not set or All (back to All each time the box opens)
  const [exportPicks, setExportPicks] = useState([]); // the projects ticked in the Export box (one or more)
  const [openRow, setOpenRow] = useState(null); // the task whose full text is shown (click a row to open or close it)
  const [sortKey, setSortKey] = useState("priority"); // default: Priority order (see val() below)
  const [sortDir, setSortDir] = useState("asc");
  const [asOf, setAsOf] = useState(""); // "" = today (live). A date shows the summary table as it stood at the end of that day.
  const [releaseDates, setReleaseDates] = useState({}); // release date of each project, like { "Main app": "2026-11-01" }

  const clickSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "updated" || key === "added" ? "desc" : "asc");
    }
  };

  // Permanent task numbers (T-001 ...). Old tasks are numbered once, oldest first;
  // the result is the same every time, so two loads cannot clash.
  const maxTaskNum = () => tasks.reduce((m, t) => Math.max(m, Number(t.num) || 0), 0);
  const nextTaskNum = () => maxTaskNum() + 1;
  async function giveMissingNumbers(list) {
    const msOf = (c) => (c && typeof c.toMillis === "function" ? c.toMillis() : c ? new Date(c).getTime() || 0 : 0);
    let n = list.reduce((m, t) => Math.max(m, Number(t.num) || 0), 0);
    const missing = list.filter((t) => !t.num).sort((a, b) => msOf(a.createdAt) - msOf(b.createdAt) || (a.id < b.id ? -1 : 1));
    const given = new Map();
    for (const t of missing) {
      n += 1;
      try { await updateDoc(doc(db, "projectTracker", t.id), { num: n }); given.set(t.id, n); }
      catch (e) { break; }
    }
    return list.map((t) => (given.has(t.id) ? { ...t, num: given.get(t.id) } : t));
  }

  const load = async () => {
    setLoading(true);
    try {
      // The Owner reads everything; everyone else asks only for their own tasks
      // (the database rule would refuse any other question).
      const ref = collection(db, "projectTracker");
      // The Owner reads everything. Krishna (Sales Head) reads everything too. Any other Sales Head reads
      // everything except Security tasks. A Sales person reads only their own tasks.
      const q = isRealOwner || (isHead && isKrishna) ? ref
        : isHead ? query(ref, where("type", "==", "Normal"))
        : query(ref, where("createdByUid", "==", user?.uid || "none"));
      const snap = await getDocs(q);
      if (isHead) {
        try {
          const ts = await getDocs(query(collection(db, "platformUsers"), where("createdBy", "==", user?.uid || "none")));
          setTeamUids(ts.docs.map((d) => d.id));
        } catch (e) { setTeamUids([]); }
      }
      let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (isRealOwner && list.some((t) => !t.num)) list = await giveMissingNumbers(list);
      setTasks(list);
    } catch (e) {
      setMsg("Error loading the Project Tracker: " + e.message);
    }
    setLoading(false);
  };

  // Release dates are kept in the Rules document (owner only), field "releaseDates".
  useEffect(() => {
    if (!isRealOwner) return;
    readRules().then((r) => setReleaseDates(r.releaseDates || {})).catch(() => {});
  }, [isRealOwner]);
  const saveReleaseDate = async (project, value) => {
    const before = releaseDates;
    setReleaseDates((d) => ({ ...d, [project]: value }));
    try {
      await setDoc(doc(db, "trackerRules", "main"), { releaseDates: { [project]: value } }, { merge: true });
    } catch (e) {
      setReleaseDates(before);
      setMsg("Error saving the release date: " + e.message);
    }
  };

  useEffect(() => { load(); }, [isOwner, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // The message at the top clears itself after 8 seconds. Error messages stay until the next action.
  useEffect(() => {
    if (!msg || /^(Error|Could not|Import stopped)/.test(msg)) return undefined;
    const t = setTimeout(() => setMsg(""), 8000);
    return () => clearTimeout(t);
  }, [msg]);

  // Sorted by the column heading the person clicked (default: Priority order:
  // 1 active work before Backlog (On hold) before Finished; 2 release: Before pilot, not set, After pilot, Later;
  // 3 status: In progress, then Pending; 4 Security before Normal; 5 oldest task number first).
  // The Owner can filter; finished (Done) tasks stay hidden until "Show done" is pressed
  // (or the Status filter is set to Done). Other users always see all their own requests.
  const rows = useMemo(
    () =>
      tasks
        .filter((t) => {
          if (!isOwner) return true;
          if (fProject !== "All" && t.project !== fProject) return false;
          if (fType !== "All" && t.type !== fType) return false;
          if (!statusOk(fStatus, t)) return false;
          if (!relOk(fRelease, t)) return false;
          if (!prioOk(fPriority, t)) return false;
          const searchWords = search.toLowerCase().split(/\s+/).filter(Boolean);
          if (t.status === "Done" && !showDone && fStatus !== "Done" && searchWords.length === 0) return false;
          if (searchWords.length > 0) {
            const hay = taskSearchText(t);
            if (!searchWords.every((w) => hay.includes(w))) return false;
          }
          return true;
        })
        .sort((a, b) => {
          const ms = (ts) =>
            ts && typeof ts.toMillis === "function" ? ts.toMillis() : ts && ts.seconds ? ts.seconds * 1000 : 0;
          const RELEASE_ORDER = { "Before pilot": 0, "After pilot": 1, Later: 2 };
          const text = (v) => (v || "").toString().toLowerCase();
          const val = (t) => {
            switch (sortKey) {
              case "title": return text(t.title);
              case "project": return text(t.project);
              case "type": return text(t.type);
              case "priority": return priorityRank(t);
              case "status": return STATUS_ORDER[t.status] ?? 9;
              case "release": return RELEASE_ORDER[t.release] ?? 9;
              case "prio": return ({ High: 0, Medium: 1, Low: 3 })[t.priority] ?? 2;
              case "target": return t.targetDate || "9999-99-99";
              case "by": return text(t.createdBy);
              case "added": return ms(t.createdAt);
              default: return ms(t.updatedAt);
            }
          };
          const isDate = sortKey === "updated" || sortKey === "added";
          const va = val(a), vb = val(b);
          // Tasks with no date always go to the bottom.
          if (isDate && (!va || !vb) && va !== vb) return !va ? 1 : -1;
          let r = typeof va === "string" ? va.localeCompare(vb) : va - vb;
          if (sortDir === "desc") r = -r;
          return r || (a.title || "").localeCompare(b.title || "");
        }),
    [tasks, isOwner, fProject, fType, fStatus, fRelease, fPriority, showDone, search, sortKey, sortDir]
  );
  // Tasks that match the Project / Type / Release choices (not Status). The boxes count these,
  // so the numbers always agree with the table.
  const baseTasks = tasks.filter((t) => {
    if (!isOwner) return true;
    if (fProject !== "All" && t.project !== fProject) return false;
    if (fType !== "All" && t.type !== fType) return false;
    if (!relOk(fRelease, t)) return false;
    if (!prioOk(fPriority, t)) return false;
    return true;
  });
  // The summary table (all tasks, open and Done). Click a number to list exactly those tasks.
  // "As of" a past date: tasks created after that day are left out, and a task finished after that day counts as Pending.
  const summary = useMemo(() => {
    if (!asOf) return buildSummaryTable(tasks);
    const end = new Date(asOf + "T23:59:59+05:30").getTime();
    const past = tasks
      .filter((t) => { const c = toMs(t.createdAt); return !c || c <= end; })
      .map((t) => {
        if (t.status !== "Done") return t;
        const closed = toMs(t.closedAt) || toMs(t.updatedAt);
        return closed && closed > end ? { ...t, status: "Pending" } : t;
      });
    return buildSummaryTable(past);
  }, [tasks, asOf]);
  const goCell = (project, release, status, type) => {
    setFProject(project); setFRelease(release); setFStatus(status); setFType(type); setFPriority("All"); setSearch("");
    setShowDone(status === "All"); // a cell with every status includes the Done tasks, so show them
  };
  // The summary table as it will be after the file is imported (nothing is saved by this).
  const afterImport = useMemo(() => {
    if (!plan || plan.fatal) return null;
    const upd = new Map();
    plan.rows.filter((r) => r.kind === "update").forEach((r) => upd.set(r.key, r.fields));
    const merged = tasks.map((t) => (upd.has(t.id) ? { ...t, ...upd.get(t.id) } : t));
    plan.rows.filter((r) => r.kind === "new").forEach((r) => merged.push({ ...r.fields }));
    return buildSummaryTable(merged);
  }, [plan, tasks]);
  const countingParts = [];
  if (isOwner) {
    if (fProject !== "All") countingParts.push(fProject);
    if (fType !== "All") countingParts.push("Type: " + fType);
    if (fRelease !== "All") countingParts.push(relLabel(fRelease));
    if (fPriority !== "All") countingParts.push("Priority: " + fPriority);
  }
  const countingText = countingParts.length ? countingParts.join(" · ") : "all projects, all types, all releases";
  const doneCount = baseTasks.filter((t) => t.status === "Done").length;

  const handleSave = async (data, adds) => {
    const stamp = new Date().toISOString();
    const entries = adds.map((text) => ({ at: stamp, text }));
    if (!isRealOwner && editing && editing.id) {
      // A Sales Head or a Sales person changes only the wording of a task they (or their team) created.
      // Status, Type, Release, Link and Note stay with the Owner (the database rule checks this too).
      await updateDoc(doc(db, "projectTracker", editing.id), {
        title: data.title, project: data.project, details: data.details,
        updatedAt: serverTimestamp(),
        history: arrayUnion({ at: stamp, text: "Edited by " + (user?.email || "") }),
      });
      setMsg("Task updated.");
    } else if (isRealOwner && editing && editing.id) {
      const update = {
        ...data,
        updatedAt: serverTimestamp(),
        closedAt: data.status === "Done" ? (editing.closedAt || stamp) : null,
      };
      if (entries.length > 0) update.history = arrayUnion(...entries);
      await updateDoc(doc(db, "projectTracker", editing.id), update);
      setMsg("Task updated.");
    } else {
      // A request from anyone else is always a Pending, Normal task (the database rule checks this too).
      const fresh = isRealOwner
        ? data
        : { ...data, type: "Normal", status: "Pending", link: "", note: "" };
      if (!isRealOwner) { delete fresh.doneWhen; delete fresh.targetDate; }
      await addDoc(collection(db, "projectTracker"), {
        ...fresh,
        ...(isRealOwner ? { num: nextTaskNum() } : {}),
        history: [{ at: stamp, text: "Created" }, ...(isRealOwner ? entries : [])],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        closedAt: fresh.status === "Done" ? stamp : null,
        createdBy: user?.email || "",
        createdByUid: user?.uid || "",
      });
      setMsg(isRealOwner ? "Task added." : "Your request was sent.");
    }
    await load();
  };

  // Date and time in India time, like 2026-10-10_0943, for the names of downloaded files.
  const today = () => {
    const p = {};
    new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(new Date()).forEach((x) => { p[x.type] = x.value; });
    return p.year + "-" + p.month + "-" + p.day + "_" + p.hour + p.minute;
  };

  // Export: the Owner ticks one or more projects. The file holds only the pending work of the ticked
  // projects (one file) and carries a lock, so Import accepts only the tasks of those projects.
  const handleExport = async () => {
    const picked = PROJECTS.filter((pr) => exportPicks.includes(pr)); // always in the order of the list
    if (picked.length === 0) { setMsg("Tick at least one project to export."); return; }
    const names = picked.join(" + ");
    const file = buildProjectsExport(tasks, picked, new Date().toISOString(), exportRelease, exportPriority);
    const relText = (exportRelease === "All" ? "" : " (" + relLabel(exportRelease) + ")") + (exportPriority === "All" ? "" : " (Priority: " + exportPriority + ")");
    if (!file || file.tasks.length === 0) { setMsg("There are no open tasks in " + names + relText + " to export."); return; }
    if (file.tasks.length > 200) { setMsg("That is " + file.tasks.length + " tasks. Import takes at most 200 tasks in one file, so please untick a project or pick one release or one priority."); return; }
    if (SECRET_WORDS.test(JSON.stringify(file)) && !window.confirm(SECRET_WARNING)) return;
    let rules = null;
    try { rules = await readRules(); } catch (e) { setMsg("Could not read the Rules: " + e.message); return; }
    if (looksLikeRealSecret(rules.text) && !window.confirm(SECRET_WARNING)) return;
    const out = withRules(file, rules);
    out.releaseDates = releaseDates;
    if (JSON.stringify(out).length > 2000000) { setMsg("The file would be over 2 MB, which Import refuses. Please untick a project or pick one release or one priority."); return; }
    setPickExport(false);
    const slug = picked.map((pr) => pr.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")).join("-");
    downloadJson(out, "project-tracker-export-" + slug + (exportRelease === "All" ? "" : "-" + exportRelease.toLowerCase().replace(/[^a-z0-9]+/g, "-")) + (exportPriority === "All" ? "" : "-priority-" + exportPriority.toLowerCase().replace(/[^a-z0-9]+/g, "-")) + "-" + today() + ".json");
    setMsg("Exported " + file.tasks.length + " open task(s) of " + names + relText + " in one file, in Priority order, plus " + (file.doneTasks ? file.doneTasks.length : 0) + " Done task(s) listed separately (Import ignores them).");
  };

  // Word button: makes the full Word file from all tasks (open and done) and the Rules. Owner only. Saves nothing.
  const handleWord = async () => {
    if (tasks.length === 0) { setMsg("There are no tasks to put in a Word file."); return; }
    let rules = null;
    try { rules = await readRules(); } catch (e) { setMsg("Could not read the Rules: " + e.message); return; }
    const risky = looksLikeRealSecret(rules.text) || tasks.some((t) => looksLikeRealSecret((t.details || "") + "\n" + (t.note || "")));
    if (risky && !window.confirm(SECRET_WARNING)) return;
    try {
      const bytes = buildWordFile(tasks, rules.text, new Date().toISOString());
      downloadBytes(bytes, "Security_Launch-Readiness_Audit_" + today() + ".docx", WORD_MIME);
      setMsg("Word file made from " + tasks.length + " task(s).");
    } catch (e) { setMsg("Could not make the Word file: " + e.message); }
  };

  // "Export N shown": saves only the tasks on the screen now (after the filters and the search), any status.
  const handleExportShown = async () => {
    if (rows.length === 0) { setMsg("There are no tasks on the screen to export."); return; }
    const file = buildShownExport(rows, new Date().toISOString(), tasks);
    if (file.tasks.length > 200) { setMsg("That is " + file.tasks.length + " open tasks. Import takes at most 200 tasks in one file, so please narrow the filters."); return; }
    if (SECRET_WORDS.test(JSON.stringify(file)) && !window.confirm(SECRET_WARNING)) return;
    let rules = null;
    try { rules = await readRules(); } catch (e) { setMsg("Could not read the Rules: " + e.message); return; }
    if (looksLikeRealSecret(rules.text) && !window.confirm(SECRET_WARNING)) return;
    const out = withRules(file, rules);
    out.releaseDates = releaseDates;
    if (JSON.stringify(out).length > 2000000) { setMsg("The file would be over 2 MB, which Import refuses. Please narrow the filters."); return; }
    downloadJson(out, "project-tracker-shown-" + rows.length + "-tasks-" + today() + ".json");
    setMsg("Exported the " + rows.length + " task(s) shown on the screen (" + file.tasks.length + " open under tasks, " + file.doneTasks.length + " Done under doneTasks), with the Rules and the summary table.");
  };

  const handleExportOne = async (task) => {
    const file = buildSingleExport(task, new Date().toISOString(), tasks);
    if (SECRET_WORDS.test(JSON.stringify(file)) && !window.confirm(SECRET_WARNING)) return;
    let rules = null;
    try { rules = await readRules(); } catch (e) { setMsg("Could not read the Rules: " + e.message); return; }
    if (looksLikeRealSecret(rules.text) && !window.confirm(SECRET_WARNING)) return;
    const out = withRules(file, rules);
    out.releaseDates = releaseDates;
    downloadJson(out, "project-tracker-task-" + (task.num ? "T-" + String(task.num).padStart(3, "0") + "-" : "") + today() + ".json");
    setMsg("Exported the task \"" + (task.title || "") + "\" with the Rules, the summary table and its complete history.");
  };

  const handlePickFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    try {
      const text = await f.text();
      setPlan(planImport(text, tasks, { projects: PROJECTS, types: TYPES, statuses: STATUSES, releases: RELEASES }));
    } catch (err) {
      setMsg("Could not read the file: " + err.message);
    }
  };

  const handleConfirmImport = async () => {
    if (!plan) return;
    const todo = plan.rows.filter((r) => r.kind === "new" || r.kind === "update");
    if (todo.length === 0) return;
    if (plan.warnings > 0 && !window.confirm(SECRET_WARNING)) return;
    setImporting(true);
    let done = 0;
    try {
      const stamp = new Date().toISOString();
      let numCounter = maxTaskNum();
      for (const r of todo) {
        const entries = r.adds.map((text) => ({ at: stamp, text }));
        if (r.kind === "update") {
          const upd = { ...r.fields, updatedAt: serverTimestamp() };
          if (r.fields.status !== undefined) upd.closedAt = r.fields.status === "Done" ? (r.closedAt || stamp) : null;
          if (entries.length > 0) upd.history = arrayUnion(...entries);
          await updateDoc(doc(db, "projectTracker", r.key), upd);
        } else {
          await addDoc(collection(db, "projectTracker"), {
            ...r.fields,
            num: ++numCounter,
            history: [{ at: stamp, text: "Created (import)" }, ...entries],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            closedAt: r.fields.status === "Done" ? stamp : null,
            createdBy: user?.email || "",
            createdByUid: user?.uid || "",
          });
        }
        done += 1;
      }
      setMsg("Import finished: " + done + " task(s) saved.");
    } catch (err) {
      setMsg("Import stopped after " + done + " of " + todo.length + " task(s): " + err.message);
    }
    setPlan(null);
    setImporting(false);
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this task and its history for good?")) return;
    await deleteDoc(doc(db, "projectTracker", id));
    setEditing(undefined);
    setMsg("Task deleted.");
    await load();
  };

  // Owner / Sales Head with tasks: the buttons sit at the right end of the filter row. Everyone else keeps them in the header.
  const buttonsInFilterRow = isOwner && tasks.length > 0;
  // In the one-line row: smaller buttons and boxes that never get squeezed.
  const BTN = buttonsInFilterRow ? { padding: "8px 10px", fontSize: 13, flexShrink: 0 } : {};
  const FR = { flexShrink: 0 };
  const SEL = { ...S.select, padding: "8px 6px", fontSize: 13 };
  const actionButtons = (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", whiteSpace: "nowrap", ...(buttonsInFilterRow ? { marginLeft: "auto" } : {}) }}>
            {isRealOwner && <button style={{ ...S.btnGray, ...BTN }} onClick={() => { setExportPriority("All"); setPickExport(true); }}>Export</button>}
            {isRealOwner && <button style={{ ...S.btnGray, ...BTN }} onClick={handleWord}>Word</button>}
            {isRealOwner && (
              <label style={{ ...S.btnGray, ...BTN, cursor: "pointer", display: "inline-block" }}>
                Import
                <input type="file" accept=".json,application/json" style={{ display: "none" }} onChange={handlePickFile} />
              </label>
            )}
            {isRealOwner && <button style={{ ...S.btnGray, ...BTN }} onClick={() => setShowRules(true)}>Rules</button>}
            <button style={{ ...S.btnBlue, ...BTN }} onClick={() => setEditing(null)}>+ New Task</button>
          </div>
  );

  const sortTh = (label, key) => (
    <th
      key={key}
      style={{ ...S.th, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
      onClick={() => clickSort(key)}
      title={"Sort by " + label}
    >
      {label}{sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );

  const viewSwitch = isRealOwner && (
    <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
      <button style={view === "tasks" ? S.btnBlue : S.btnGray} onClick={() => setView("tasks")}>Tasks</button>
      <button style={view === "docs" ? S.btnBlue : S.btnGray} onClick={() => setView("docs")}>Docs</button>
      <button style={view === "audit" ? S.btnBlue : S.btnGray} onClick={() => setView("audit")}>Audit</button>
    </div>
  );

  if ((view === "docs" || view === "audit") && isRealOwner) {
    return (
      <div style={S.wrap}>
        <div style={S.header}><div style={S.title}>Project Tracker</div></div>
        {viewSwitch}
        {view === "audit" ? <TrackerAuditTab /> : <TrackerDocsTab />}
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={S.title}>{isOwner ? "🗂 Project Tracker" : "🗂 My Task Requests"}</div>
        {!buttonsInFilterRow && actionButtons}
      </div>

      {viewSwitch}

      {msg && <div style={S.msg}>{msg}</div>}

      {isRealOwner && showRules && <RulesModal onClose={() => setShowRules(false)} onMsg={setMsg} />}

      {isOwner && !loading && tasks.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: "#e8eaf6", marginBottom: 8 }}>
            <b>Summary</b> · all tasks, project by project. Pending = To do + In progress + Needs check + On hold. Click a number to list those tasks.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: "#c8cce0" }}>Numbers as of</span>
            <DateBox
              style={{ width: 150, padding: "4px 8px", fontSize: 13 }}
              value={asOf}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(v) => setAsOf(v)}
              placeholder="Pick a date"
            />
            {asOf && <button style={S.btnSmall} onClick={() => setAsOf("")}>Today</button>}
            <span style={{ fontSize: 12, color: asOf ? "#fbbf24" : "#7b82a0" }}>
              {asOf ? "Past view (read-only): tasks added later are left out; tasks finished later count as Pending. Clicking is off." : "Today (live). Pick a date to see the numbers of that day."}
            </span>
          </div>
          {isHead && !isKrishna && (
            <div style={{ fontSize: 12, color: "#fbbf24", marginBottom: 8 }}>Security tasks are not shown to you, so they are not counted in these numbers.</div>
          )}
          <SummaryGrid table={summary} onCell={asOf ? undefined : goCell} dates={isRealOwner ? releaseDates : undefined} onDate={isRealOwner ? saveReleaseDate : undefined} />
        </div>
      )}

      {isOwner && tasks.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
        {/* filters-two-rows: row 1 = the filters and the search, row 2 = the buttons */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={FR}>
            <label style={S.label}>Project</label>
            <select style={SEL} value={fProject} onChange={(e) => setFProject(e.target.value)}>
              {["All", ...PROJECTS].map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div style={FR}>
            <label style={S.label}>Type</label>
            <select style={SEL} value={fType} onChange={(e) => setFType(e.target.value)}>
              {["All", ...TYPES].map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div style={FR}>
            <label style={S.label}>Status</label>
            <select style={SEL} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
              {["All", "Open", "Pending", "Doing", "Check", "Parked", "Done"].map((p) => <option key={p} value={p}>{FILTER_STATUS_LABEL[p]}</option>)}
            </select>
          </div>
          <div style={FR}>
            <label style={S.label}>Release</label>
            <select style={SEL} value={fRelease} onChange={(e) => setFRelease(e.target.value)}>
              {["All", ...RELEASES, "Not set"].map((p) => <option key={p} value={p}>{relLabel(p)}</option>)}
            </select>
          </div>
          <div style={FR}>
            <label style={S.label}>Priority</label>
            <select style={SEL} value={fPriority} onChange={(e) => setFPriority(e.target.value)}>
              {["All", ...PRIORITIES, "Not set"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div style={FR}>
            <label style={S.label}>Search</label>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <input
                type="text"
                style={{ ...S.input, width: 260, padding: "6px 8px", fontSize: 13 }}
                placeholder="Any word: T-162, wallet, CRM…"
                title="Finds tasks that have all these words anywhere: number, title, project, type, status, release, link, note, details, added by"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && <button style={{ ...S.btnSmall, flexShrink: 0 }} onClick={() => setSearch("")} title="Clear the search">✕</button>}
            </div>
          </div>
          {(fProject !== "All" || fType !== "All" || fStatus !== "All" || fRelease !== "All" || fPriority !== "All" || search !== "") && (
            <button
              style={{ ...S.btnSmall, flexShrink: 0 }}
              onClick={() => { setFProject("All"); setFType("All"); setFStatus("All"); setFRelease("All"); setFPriority("All"); setSearch(""); }}
            >
              Clear filters
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {isRealOwner && (
            <button style={{ ...S.btnGray, ...BTN }} onClick={handleExportShown} title="Save only the tasks shown in the list below">
              {"Export " + rows.length + " shown"}
            </button>
          )}
          <button style={{ ...S.btnGray, ...BTN }} onClick={() => setShowDone((v) => !v)}>
            {showDone ? "Hide done" : "Show done (" + doneCount + ")"}
          </button>
          {buttonsInFilterRow && actionButtons}
        </div>
        </div>
      )}

      {isOwner && !loading && tasks.length > 0 && (
        <div style={{ fontSize: 13, color: "#7b82a0", marginBottom: 14 }}>
          The list below shows {rows.length} task(s) · {countingText}{fStatus !== "All" ? " · Status: " + FILTER_STATUS_LABEL[fStatus] : ""}{search.trim() ? " · Search: \"" + search.trim() + "\" (Done tasks included)" : ""}.
          {!showDone && fStatus !== "Done" && !search.trim() && doneCount > 0 ? " Done tasks are hidden: press Show done (" + doneCount + ")." : ""}
        </div>
      )}

      {!isOwner && !loading && tasks.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, color: "#e8eaf6", marginBottom: 8 }}>
            Counting: <b>{countingText}</b>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            {STATUSES.map((st) => {
              const n = baseTasks.filter((t) => t.status === st).length;
              const active = isOwner && fStatus === st;
              return (
                <div
                  key={st}
                  onClick={isOwner ? () => setFStatus(active ? "All" : st) : undefined}
                  title={isOwner ? "Click to list only these tasks" : undefined}
                  style={{
                    border: "1px solid " + STATUS_COLOR[st], borderRadius: 10, padding: "8px 16px", minWidth: 120,
                    textAlign: "center", cursor: isOwner ? "pointer" : "default",
                    background: active ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)",
                  }}
                >
                  <div style={{ fontSize: 26, fontWeight: 700, color: STATUS_COLOR[st], lineHeight: 1.2 }}>{n}</div>
                  <div style={{ fontSize: 12, color: "#c8cce0" }}>{STATUS_LABEL[st] || st}</div>
                </div>
              );
            })}
            <div style={{ border: "1px solid #2a2d3e", borderRadius: 10, padding: "8px 16px", minWidth: 120, textAlign: "center" }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: "#e8eaf6", lineHeight: 1.2 }}>{baseTasks.length}</div>
              <div style={{ fontSize: 12, color: "#c8cce0" }}>All tasks</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "#7b82a0" }}>
            The list below shows {rows.length} task(s){isOwner && fStatus !== "All" ? " (Status: " + STATUS_LABEL[fStatus] + ")" : ""}.
            {isOwner && !showDone && fStatus !== "Done" && doneCount > 0 ? " Done tasks are hidden. Click the green Done box to see them." : ""}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: "#7b82a0" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: "#7b82a0" }}>{tasks.length > 0
          ? "No task matches these filters or this search. Press Show done, clear the search, or set the filters to All."
          : isOwner ? "No tasks yet." : "You have not sent any task requests yet."}</div>
      ) : (
        <div style={{ ...S.tableWrap, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: isOwner ? 1240 : 560 }}>
            <thead>
              <tr>
                <th
                  style={{ ...S.th, width: 64, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                  onClick={() => clickSort("priority")}
                  title="Sort by Priority order"
                >
                  No.{sortKey === "priority" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
                {sortTh("Task", "title")}{sortTh("Project", "project")}
                {isOwner && sortTh("Type", "type")}
                {sortTh("Status", "status")}
                {isOwner && sortTh("Release", "release")}
                {isOwner && sortTh("Priority", "prio")}
                {isOwner && sortTh("Target", "target")}
                {isOwner && <th style={S.th}>Link</th>}
                <th style={S.th}>Note</th>
                {isOwner && sortTh("By", "by")}
                {isOwner && sortTh("Added", "added")}
                {isOwner && sortTh("Updated", "updated")}
              </tr>
            </thead>
            <tbody>
              {rows.map((t, idx) => {
                const open = openRow === t.id;
                // One line per task: long text ends with "...". Click the row to show the full text (click again to close).
                const cut = (w) => (open ? { whiteSpace: "normal", wordBreak: "break-word", maxWidth: w } : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: w });
                return (
                <tr
                  key={t.id}
                  style={{ opacity: t.status === "Done" ? 0.55 : 1, cursor: "pointer", background: open ? "rgba(255,255,255,0.04)" : undefined }}
                  onClick={() => setOpenRow(open ? null : t.id)}
                  title={open ? "Click to close" : "Click to see the full text"}
                >
                  <td style={{ ...S.td, color: "#7b82a0", whiteSpace: "nowrap", verticalAlign: "top" }}>{t.num ? "T-" + String(t.num).padStart(3, "0") : "—"}</td>
                  <td
                    style={{ ...S.td, verticalAlign: "top", ...cut(300), cursor: canEdit(t) ? "pointer" : "default" }}
                    onClick={canEdit(t) ? (e) => { e.stopPropagation(); setEditing(t); } : undefined}
                    title={canEdit(t) ? "Click to edit this task" : undefined}
                  >
                    {t.title}
                    {open && t.doneWhen && <div style={{ fontSize: 12, color: "#3ecf8e", marginTop: 4 }}>Done when: {t.doneWhen}</div>}
                  </td>
                  <td style={{ ...S.td, whiteSpace: "nowrap", verticalAlign: "top" }}>{t.project}</td>
                  {isOwner && <td style={{ ...S.td, verticalAlign: "top" }}><span style={{ ...S.badge(TYPE_COLOR[t.type] || "#7b82a0"), whiteSpace: "nowrap" }}>{t.type}</span></td>}
                  <td style={{ ...S.td, verticalAlign: "top" }}><span style={{ ...S.badge(STATUS_COLOR[t.status] || "#7b82a0"), whiteSpace: "nowrap" }}>{STATUS_LABEL[t.status] || t.status}</span></td>
                  {isOwner && <td style={{ ...S.td, verticalAlign: "top" }}>{t.release ? <span style={{ ...S.badge(RELEASE_COLOR[t.release] || "#7b82a0"), whiteSpace: "nowrap" }}>{relLabel(t.release)}</span> : <span style={{ color: "#7b82a0" }}>Not tasks</span>}</td>}
                  {isOwner && <td style={{ ...S.td, verticalAlign: "top" }}>{t.priority ? <span style={{ ...S.badge(PRIORITY_COLOR[t.priority] || "#7b82a0"), whiteSpace: "nowrap" }}>{t.priority}</span> : <span style={{ color: "#7b82a0" }}>—</span>}</td>}
                  {isOwner && <td style={{ ...S.td, color: targetColor(t), whiteSpace: "nowrap", verticalAlign: "top" }} title={t.targetDate && t.status !== "Done" && t.targetDate < todayIst() ? "Late: the Target date has passed" : undefined}>{t.targetDate ? fmtTarget(t.targetDate) : "—"}</td>}
                  {isOwner && <td style={{ ...S.td, color: "#7b82a0", verticalAlign: "top", ...cut(140) }}>{t.link || "—"}</td>}
                  <td style={{ ...S.td, color: "#7b82a0", verticalAlign: "top", ...cut(360) }}>{t.note || "—"}</td>
                  {isOwner && <td style={{ ...S.td, color: "#7b82a0", verticalAlign: "top", ...cut(180) }}>{t.createdBy || "—"}</td>}
                  {isOwner && <td style={{ ...S.td, color: "#7b82a0", whiteSpace: "nowrap", verticalAlign: "top" }}>{fmtUpdated(t.createdAt)}</td>}
                  {isOwner && <td style={{ ...S.td, color: "#7b82a0", whiteSpace: "nowrap", verticalAlign: "top" }}>{fmtUpdated(t.updatedAt)}</td>}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pickExport && (() => {
        const countOf = (pr) => tasks.filter((t) => t.project === pr && t.status !== "Done" && relOk(exportRelease, t) && prioOk(exportPriority, t)).length;
        const ticked = PROJECTS.filter((pr) => exportPicks.includes(pr));
        const total = ticked.reduce((n, pr) => n + countOf(pr), 0);
        const allTicked = ticked.length === PROJECTS.length;
        const toggle = (pr) => setExportPicks((cur) => (cur.includes(pr) ? cur.filter((x) => x !== pr) : [...cur, pr]));
        return (
          <div style={S.modalOverlay} onClick={() => setPickExport(false)}>
            <div style={{ ...S.modalBox, maxWidth: 1040 }} onClick={(e) => e.stopPropagation()}>
              <div style={S.header}>
                <div style={S.title}>Export: which projects?</div>
                <button style={S.btnSmall} onClick={() => setPickExport(false)}>✕ Close</button>
              </div>
              <div style={{ fontSize: 13, color: "#c8cce0", marginBottom: 12 }}>
                Tick one or more projects. You get one file with the Pending tasks (To do, In progress, Needs check, On hold) of the projects, release and priority you choose, in Priority order, plus the Done tasks of the same projects, release and priority (listed separately) and the summary table of all tasks. Import will accept only the ticked projects.
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: "#8b90a8", minWidth: 64 }}>Projects</div>
                {PROJECTS.map((pr) => {
                  const on = exportPicks.includes(pr);
                  return (
                    <label
                      key={pr}
                      style={{
                        display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                        border: "1px solid " + (on ? "#6c63ff" : "#2a2a45"), background: on ? "#6c63ff22" : "transparent", color: "#e8e8f0", fontSize: 14,
                      }}
                    >
                      <input type="checkbox" checked={on} onChange={() => toggle(pr)} />
                      <span>{pr}</span>
                      <span style={{ fontSize: 12, color: "#8b90a8" }}>{countOf(pr)} open</span>
                    </label>
                  );
                })}
                <button style={S.btnSmall} onClick={() => setExportPicks(allTicked ? [] : [...PROJECTS])}>
                  {allTicked ? "Untick all" : "Select everything"}
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: "#8b90a8", minWidth: 64 }}>Release</div>
                {[...RELEASES, "Not set", "All"].map((r) => (
                  <button
                    key={r}
                    style={{ ...S.btnSmall, ...(exportRelease === r ? { background: "#6c63ff", color: "#fff", borderColor: "#6c63ff" } : {}) }}
                    onClick={() => setExportRelease(r)}
                  >{relLabel(r)}</button>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: "#8b90a8", minWidth: 64 }}>Priority</div>
                {["All", ...PRIORITIES, "Not set"].map((r) => (
                  <button
                    key={r}
                    style={{ ...S.btnSmall, ...(exportPriority === r ? { background: "#6c63ff", color: "#fff", borderColor: "#6c63ff" } : {}) }}
                    onClick={() => setExportPriority(r)}
                  >{r}</button>
                ))}
              </div>

              <button
                style={{ ...S.btnBlue, width: "100%", opacity: ticked.length === 0 ? 0.4 : 1, cursor: ticked.length === 0 ? "not-allowed" : "pointer" }}
                disabled={ticked.length === 0}
                onClick={handleExport}
              >
                {ticked.length === 0 ? "Tick a project to export" : "Export " + total + " open task" + (total === 1 ? "" : "s") + " (" + ticked.length + " project" + (ticked.length === 1 ? "" : "s") + ")"}
              </button>
            </div>
          </div>
        );
      })()}

      {plan && (
        <div style={S.modalOverlay} onClick={() => setPlan(null)}>
          <div style={{ ...S.modalBox, maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
            <div style={S.header}>
              <div style={S.title}>Import preview</div>
              <button style={S.btnSmall} onClick={() => setPlan(null)}>✕ Close</button>
            </div>
            <div style={{ fontSize: 13, color: "#c8cce0", marginBottom: 12 }}>
              Dry run only. Nothing has been saved yet.
            </div>
            {!plan.fatal && plan.locks && plan.locks.length > 0 && (
              <div style={{ fontSize: 13, marginBottom: 12, color: "#fbbf24" }}>
                This file is only for <b>{plan.locks.join(", ")}</b>. Tasks of any other project are skipped.
              </div>
            )}
            {plan.fatal ? (
              <div style={{ ...S.msg, borderColor: "#f87171", color: "#f87171" }}>{plan.fatal}</div>
            ) : (
              <>
                <div style={{ fontSize: 13, marginBottom: 12 }}>
                  New: <b>{plan.counts.new}</b> · Updates: <b>{plan.counts.update}</b> · No change: <b>{plan.counts.same}</b> ·
                  Problems: <b style={{ color: plan.counts.problem ? "#f87171" : undefined }}>{plan.counts.problem}</b> ·
                  Secret-word warnings: <b style={{ color: plan.warnings ? "#fbbf24" : undefined }}>{plan.warnings}</b>
                </div>
                {afterImport && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#8b90a8", marginBottom: 6 }}>Summary table after this import (a number that changes shows "was" and the old number)</div>
                    <SummaryGrid table={afterImport} base={summary} />
                  </div>
                )}
                <div style={{ maxHeight: 360, overflowY: "auto", marginBottom: 12 }}>
                  {plan.rows.map((r) => (
                    <div key={r.n} style={{ padding: "6px 0", borderBottom: "1px solid #2a2d3e", fontSize: 12 }}>
                      <span style={S.badge(r.kind === "new" ? "#3ecf8e" : r.kind === "update" ? "#4f8ef7" : r.kind === "problem" ? "#f87171" : "#7b82a0")}>
                        {r.kind === "new" ? "New" : r.kind === "update" ? "Update" : r.kind === "problem" ? "Problem" : "No change"}
                      </span>{" "}
                      <span style={{ color: "#e8eaf6" }}>{r.n}. {r.num ? r.num + " · " : ""}{r.title || "(no title)"}</span>
                      {r.changes.length > 0 && <div style={{ color: "#7b82a0" }}>Changes: {r.changes.join("; ")}</div>}
                      {r.kind === "new" && <div style={{ color: "#7b82a0" }}>{r.fields.project} · {r.fields.type} · {r.fields.status}</div>}
                      {r.problems.map((p, i) => <div key={"p" + i} style={{ color: "#f87171" }}>{p}</div>)}
                      {r.warnings.map((w, i) => <div key={"w" + i} style={{ color: "#fbbf24" }}>{w}</div>)}
                      {r.notes.map((w, i) => <div key={"n" + i} style={{ color: "#7b82a0" }}>{w}</div>)}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: "#7b82a0", marginBottom: 12 }}>
                  Rows marked Problem are skipped. Import never deletes anything.
                </div>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button style={S.btnGray} onClick={() => setPlan(null)}>Cancel</button>
              {!plan.fatal && (
                <button
                  style={S.btnBlue}
                  onClick={handleConfirmImport}
                  disabled={importing || plan.counts.new + plan.counts.update === 0}
                >
                  {importing ? "Saving…" : "Confirm import (" + (plan.counts.new + plan.counts.update) + ")"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {editing !== undefined && (
        <TrackerModal
          task={editing}
          isOwner={isRealOwner}
          onSave={handleSave}
          onClose={() => setEditing(undefined)}
          onDelete={handleDelete}
          onExportOne={handleExportOne}
        />
      )}
    </div>
  );
}